<template>
  <layout v-statusbar.hide>
    <div class="interaction-button">
      <interaction-mode-button
        :hasNotification="
          !hasSeenDreamTeams &&
            !isElectionInInteractiveMode &&
            currentLevelHasElectionData
        "
        :isInInteractiveMode="isElectionInInteractiveMode"
        :disabled="!currentLevelHasElectionData"
        :class="{
          'animate-breathe':
            !isElectionInInteractiveMode && currentLevelHasElectionData
        }"
      />
    </div>
    <div class="settings-button">
      <settings-menu :returningToMenu="showMenu" />
    </div>

    <content-scroll-container
      :scroll="false"
      :overflow-visible="true"
      class="content-scroll-container"
    >
      <content-scroll-inner class="h-full flex flex-col justify-center">
        <button class="relative mb-2">
          <img
            v-if="constituencyLevel === 'cantonal'"
            :src="flagUrl[constituencyLevel]"
            class="inline-block canton-icon rounded-full border-2 border-white"
          />
          <img
            v-else
            :src="flagUrl[constituencyLevel]"
            class="inline-block canton-icon"
          />
        </button>
        <p class="text-chplus-tooltip" v-if="!electionTime">
          {{ electionDateText }}
        </p>
        <button
          v-if="displayedElectionYears.length > 0"
          @click="onYearSelectionClick"
          :disabled="displayedElectionYears.length === 1"
          :class="{ 'election-time': electionTime }"
          class="year-button relative text-chplus-tooltip bg-transparent text-center py-1 my-2 px-1 rounded-md self-center darker-border"
        >
          <icon-notification
            v-if="
              ((!hasSeenYearSelector && hasSwitchedLevel) ||
                hasMissingProgressInNewestYear) &&
                displayedElectionYears.length > 1
            "
            class="icon-notification"
          />
          <template v-if="electionTime">
            <p class="text-chplus-tooltip">{{ electionDateText }}</p>
            <count-down :end="electionTime" />
          </template>
          <span v-else>{{ electionYear }}</span>
        </button>

        <navigation-title-box
          mode="light"
          :allow-new-elections="canSwitchCouncil"
          :style="{
            opacity: isElectionInInteractiveMode ? 1 : 0
          }"
          class="mt-1"
          :lightBackgroundColor="
            `rgba(255,255,255,${0.2 +
              selectedElection[constituencyLevel] * 0.15})`
          "
        />
        <circular-menu
          v-if="isElectionInInteractiveMode"
          class="mx-auto mb-2"
          :highlightOpacity="0.2 + selectedElection[constituencyLevel] * 0.15"
          :levelSwitchBus="levelSwitchBus"
        ></circular-menu>
        <circular-menu-informative
          v-else
          :informative-content="
            $t(`views.main.information.${constituencyLevel}`)
          "
          :style="isMobileScreen ? '' : 'cursor: default;'"
          @click="onInformationClick"
          :levelSwitchBus="levelSwitchBus"
        />

        <router-link
          :to="{ name: 'profile' }"
          v-if="hasSeenResults && !allAnswered"
          class="not-all-answered-hint block mx-8"
          :class="{
            hidden: !isElectionInInteractiveMode
          }"
          :style="{
            position: 'relative',
            zIndex: 9000
          }"
        >
          <icon-alert />
          {{ $t("views.home.info_profile_incomplete") }}
        </router-link>
      </content-scroll-inner>
    </content-scroll-container>
    <template v-slot:left>
      <InformationText
        :title="$t(`views.information.election.${constituencyLevel}.title`)"
        :subtitle="
          $t(`views.information.election.${constituencyLevel}.subtitle`)
        "
        :content="$t(`views.information.election.${constituencyLevel}.content`)"
        :link="
          $t(`views.information.election.${constituencyLevel}.button.link`)
        "
        :linkLabel="
          $t(`views.information.election.${constituencyLevel}.button.label`)
        "
        containerClass="h-screen w-full bg-white text-left p-20 text-color-candidate text-chplus-l flex flex-col gap-8 overflow-y-auto"
      />
    </template>
    <template v-slot:bottom>
      <home-navigation
        onElectionPage
        :activeSurvey="activeSurvey"
        :canAnswerSurvey="canAnswerSurvey"
        @survey-click="showSurvey = true"
      >
      </home-navigation>
    </template>
    <survey
      v-if="activeSurvey != null"
      :show="showSurvey"
      :survey-id="activeSurvey.id"
      :levelSwitchBus="levelSwitchBus"
      class="absolute top-0 left-0 w-full h-full"
      ><template v-slot:close-action>
        <button @click="showSurvey = false">
          <icon-close class="text-3xl" /></button></template
    ></survey>
    <language-not-available-alert
      v-if="showWarningLanguageNotAvailable"
      :show="showWarningLanguageNotAvailable"
      :selectedContentLanguage="contentLanguage"
      :availableContentLanguage="electionContentLanguage"
      @continue="
        $store.commit('user/setHasSeenLanguageNotAvailableWarning', true)
      "
    />
    <debug-menu />
    <level-switch
      :hasDataAvailableForLevel="hasElectionForLevel"
      :levelSwitchBus="levelSwitchBus"
      :showNotificationForLevel="showNotificationLevelSwitch"
      :finishedTutorial="hasSeenAll"
    />
    <year-selector-modal
      v-if="showElectionYearSelection"
      @close="showElectionYearSelection = false"
      @accept="onElectionYearChange"
      :years="displayedElectionYears"
      :selected="electionYear"
      :show-notification="hasMissingProgressInNewestYear"
    />
  </layout>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import Vue from "vue";
import { ElectionProgressStepId } from "@/utils/ElectionProgressStepId";

import store from "@/store";
import dayjs from "dayjs";

import IconAlert from "@/components/icons/IconAlert";
import IconClose from "@/components/icons/IconCircleReject";
import ContentScrollContainer from "@/components/layouts/ContentScrollContainer";
import ContentScrollInner from "@/components/layouts/ContentScrollInner";
import HomeNavigation from "@/components/layouts/HomeNavigation.vue";
import LanguageNotAvailableAlert from "@/components/layouts/LanguageNotAvailableAlert";
import InteractionModeButton from "@/components/buttons/InteractionModeButton";
import Layout from "@/components/layouts/LayoutHome";
import Survey from "@/components/layouts/Survey";
import NavigationTitleBox from "@/components/nav/NavigationTitleBox";
import SettingsMenu from "@/components/nav/SettingsMenu";
import CircularMenu from "@/components/nav/circular/CircularMenu";
import CircularMenuInformative from "@/components/nav/circular/CircularMenuInformative";
import DebugMenu from "@/components/user/DebugMenu.vue";
import { ProgressState } from "@/utils/ProgressState";
import { ProgressStepId } from "@/utils/ProgressStepId";
import LevelSwitch from "@/components/buttons/LevelSwitch.vue";
import CountDown from "../components/misc/CountDown.vue";
import YearSelectorModal from "../components/modals/YearSelectorModal.vue";
import IconNotification from "@/components/icons/IconNotification";
import StatusBarDirective from "@/directives/StatusBarDirective";
import IsMobileMixin from "@/utils/IsMobileMixin";
import InformationText from "../components/misc/InformationText.vue";

export default {
  name: "ElectionHome",
  directives: {
    statusbar: StatusBarDirective
  },
  mixins: [IsMobileMixin],
  components: {
    ContentScrollInner,
    LanguageNotAvailableAlert,
    Survey,
    IconAlert,
    ContentScrollContainer,
    Layout,
    SettingsMenu,
    CircularMenu,
    IconClose,
    NavigationTitleBox,
    HomeNavigation,
    DebugMenu,
    CircularMenuInformative,
    InteractionModeButton,
    LevelSwitch,
    CountDown,
    YearSelectorModal,
    IconNotification,
    InformationText
  },
  async beforeRouteEnter(to, from, next) {
    const user = store.getters["user/user"];
    if (!user.constituencyId) {
      next({ name: "welcome" });
      return;
    }

    await Promise.all([
      store.dispatch("election/fetchElections"),
      store.dispatch("location/fetchCantons"),
      store.dispatch("user/fetchProgression"),
      store.dispatch("party/fetchParties"),
      store.dispatch("survey/fetchSurveys")
    ]);

    // Active election must be set before questions/responses are fetched
    await Promise.all([
      store.dispatch("questionnaire/fetchQuestions"),
      store.dispatch("questionnaire/fetchResponses"),
      store.dispatch("questionnaire/fetchSmartSpider")
    ]);

    if (store.getters["election/activeElection"] == null) {
      next({ name: "location" });
      return;
    }
    let menuRoutes = [
      "/voting-instructions",
      "/our-system",
      "/get-involved",
      "/settings",
      "/credits"
    ];
    if (menuRoutes.includes(from.path)) {
      next(vm => {
        vm.showMenu = true;
      });
      return;
    }

    next();
  },
  data: () => ({
    showSurvey: false,
    showMenu: false,
    showElectionYearSelection: false,
    levelSwitchBus: new Vue()
  }),
  async created() {
    const hasOpenedElectionHome = this.hasProgress(ProgressStepId.ElectionHome);
    store.dispatch("location/fetchConstituencies", {
      cantonId: this.user.cantonId
    });
    if (!hasOpenedElectionHome) {
      await this.updateProgression({
        key: ProgressStepId.ElectionHome,
        data: { state: ProgressState.Completed }
      });
    }

    if (this.isMobile) {
      store.dispatch("notifications/fetchNotifications");
      store.dispatch("notifications/requestPermission");
    }

    this.levelSwitchBus.$emit("location-switch", "election-home");
  },
  async mounted() {
    await Promise.all([
      store.dispatch("candidate/fetchCandidates"),
      store.dispatch("candidate/fetchMatches")
    ]);

    this.setSelectedMenu("election-home");
  },
  computed: {
    ...mapGetters("user", [
      "user",
      "hasProgress",
      "contentLanguage",
      "electionContentLanguage",
      "hasSeenLanguageNotAvailableWarning"
    ]),
    ...mapGetters("location", ["canton", "constituency"]),
    ...mapGetters("survey", ["activeSurvey"]),
    ...mapGetters("election", [
      "election",
      "elections",
      "activeElection",
      "hasElectionProgress",
      "hasElectionForLevel",
      "electionYear",
      "electionYearPerLevel",
      "electionYearsCurrentLevel",
      "electionsPerLevelPerYear",
      "hasNewestElection",
      "hasNewCurrentElection"
    ]),
    ...mapGetters("questionnaire", ["shortAnswered", "allAnswered"]),
    ...mapGetters("list", ["lists"]),
    ...mapGetters("viewState", [
      "isInInteractiveMode",
      "constituencyLevel",
      "selectedElection",
      "flagUrl"
    ]),
    hasOfficialLists() {
      return this.lists.filter(l => l.adapted === null).length > 0;
    },
    hasSeenAll() {
      return this.hasProgress(ProgressStepId.SeenAll);
    },
    hasSeenListsTutorial() {
      return this.hasProgress(ProgressStepId.TutorialLists);
    },
    hasFinishedElectionTutorial() {
      return (
        this.elections.every(id =>
          this.hasElectionProgress(ElectionProgressStepId.Match, id)
        ) &&
        this.hasFavoriteList &&
        (!this.hasOfficialLists || this.hasSeenListsTutorial)
      );
    },
    displayedElectionYears() {
      return [...this.electionYearsCurrentLevel].sort((a, b) => b - a);
    },
    electionDateText() {
      let text = this.$t(`views.home.election.${this.constituencyLevel}`);

      if (this.constituencyLevel === "communal") {
        text = `${this.constituency(this.user.constituencyId)?.name}\n${text}`;
      }

      if (
        this.isElectionInInteractiveMode &&
        this.electionTime &&
        this.electionTime > new Date()
      ) {
        text += `: ${dayjs(this.electionTime).format("DD MMM YYYY")}`;
      }

      return text;
    },
    electionTime() {
      if (!this.activeElection?.nextElectionDate) {
        return null;
      }

      const electionDate = new Date(this.activeElection.nextElectionDate);
      if (
        electionDate.getFullYear() != this.electionYear ||
        this.activeElection.level != this.constituencyLevel ||
        electionDate < new Date()
      ) {
        return null;
      }

      // All elections end at 12
      electionDate.setHours(12, 0, 0, 0);

      return electionDate;
    },
    showWarningLanguageNotAvailable() {
      return (
        this.electionContentLanguage !== this.contentLanguage &&
        !this.hasSeenLanguageNotAvailableWarning
      );
    },
    onGovPage() {
      return this.$route.name == "government-home";
    },
    onElectionPage() {
      return this.onElectionHomePage;
    },
    onInfoPage() {
      return this.onElectionHomePage;
    },
    onElectionHomePage() {
      return this.$route.name == "election-home";
    },
    currentLevelHasElectionData() {
      return this.hasElectionForLevel(this.constituencyLevel);
    },
    isElectionInInteractiveMode() {
      return this.isInInteractiveMode && this.currentLevelHasElectionData;
    },
    hasAnsweredQuestions() {
      return (
        this.hasProgress(ProgressStepId.Questionnaire) && this.shortAnswered
      );
    },
    hasSeenResults() {
      return this.hasProgress(ProgressStepId.Results);
    },
    canSwitchCouncil() {
      return this.hasSeenResults;
    },
    canAnswerSurvey() {
      return true;
    },
    hasSeenDreamTeams() {
      return this.hasProgress(ProgressStepId.DreamTeams);
    },
    hasFavoriteList() {
      return this.hasProgress(ProgressStepId.FavoriteList);
    },
    hasSwitchedLevel() {
      return this.hasProgress(ProgressStepId.LevelSwitch);
    },
    hasSeenYearSelector() {
      return this.hasProgress(ProgressStepId.YearSelector);
    },
    hasMissingProgressInNewestYear() {
      const levelElections = this.electionsPerLevelPerYear[
        this.constituencyLevel
      ];
      const years = Object.keys(levelElections).sort((a, b) => b - a);

      if (years.length === 0) {
        return false;
      }

      const newestYear = years[0];

      return (
        newestYear != this.electionYear &&
        levelElections[newestYear].some(
          election =>
            !this.hasElectionProgress(
              ElectionProgressStepId.Match,
              parseInt(election.id)
            )
        )
      );
    }
  },
  methods: {
    ...mapActions(["store", "get"]),
    ...mapActions("user", ["updateUser", "updateProgression"]),
    ...mapActions("election", ["setElectionYear"]),
    ...mapMutations("viewState", ["setSelectedMenu"]),
    onYearSelectionClick() {
      if (!this.hasSeenYearSelector && this.hasSwitchedLevel) {
        this.updateProgression({
          key: ProgressStepId.YearSelector,
          data: { state: ProgressState.Completed }
        });
      }

      this.showElectionYearSelection = true;
    },
    showNotificationLevelSwitch(level) {
      const notSameConstituency = level !== this.constituencyLevel;
      const isFederalOrCantonal = ["federal", "cantonal"].includes(level);
      const hasNewestElection =
        !this.hasProgress(ProgressStepId.SeenAll) &&
        this.hasNewestElection(level);
      const hasNewCurrentElection =
        this.hasSeenDreamTeams && this.hasNewCurrentElection(level);
      const justFinishedElectionTutorial =
        !this.hasProgress(ProgressStepId.LevelSwitchBack) &&
        this.hasProgress(ProgressStepId.SeenAll);
      return (
        notSameConstituency &&
        (hasNewestElection ||
          hasNewCurrentElection ||
          (isFederalOrCantonal && justFinishedElectionTutorial))
      );
    },
    onElectionYearChange(year) {
      this.setElectionYear(year);
      this.showElectionYearSelection = false;
    },
    goToGov() {
      if (!this.onGovPage) {
        this.$router.push({ name: "government-home" });
      }
    },
    goToElection() {
      if (!this.onElectionHomePage) {
        this.$router.push({ name: "election-home" });
      }
    },
    onInformationClick() {
      if (!this.isMobileScreen) {
        return;
      }

      this.$router.push({
        name: "information",
        params: {
          resource: "election",
          level: this.constituencyLevel
        }
      });
    }
  }
};
</script>

<style scoped>
.year-button {
  min-width: 12em;
}

.year-button.election-time {
  min-width: min(24rem, 90%);
}

.year-button:disabled {
  border: transparent;
}

.icon-notification {
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(25%, -25%);
}

.darker-border {
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.content-scroll-container {
  margin-bottom: 3.6885rem;
}

.circular-menu {
  width: 24rem;
  height: 24rem;
  max-width: 24rem;
  max-height: 24rem;
  margin-left: auto;
  margin-right: auto;
}

@media (min-width: 1024px) {
  .circular-menu {
    width: 30rem;
    height: 30rem;
    max-height: 30rem;
    max-width: 30rem;
  }
}

.canton-icon {
  height: 4.5rem;
  width: 4.5rem;
}

.circular-menu {
  margin-top: 2.5rem;
}

select:disabled {
  appearance: none;
  line-height: normal;
}

.not-all-answered-hint {
  margin-top: 2rem;
}

@media (min-height: 1024px) {
  .canton-icon {
    height: 5rem;
    widows: 5rem;
  }

  .content-scroll-container {
    margin-bottom: 4.17525rem;
  }
}

@media (min-width: 768px) and (min-height: 1024px) {
  .circular-menu {
    width: 35rem;
  }

  .bottom-nav-icon {
    height: 2rem;
    font-size: 2rem;
  }
}

@media (max-width: 380px), (max-height: 600px) {
  .content-scroll-container {
    margin-bottom: 2.6885rem;
  }

  .circular-menu {
    margin-top: 1rem;
  }

  .circular-menu {
    width: 26rem;
    max-width: 100%;
  }

  .not-all-answered-hint {
    margin-top: 1rem;
  }

  .bottom-nav-label {
    font-size: 1.15em;
  }
}

.interaction-button {
  position: absolute;
  top: 1.64rem;
  left: 0.64rem;
}

.settings-button {
  position: absolute;
  top: 1.64rem;
  right: 0.64rem;
  z-index: 30;
}

@media (min-width: 1024px) {
  .interaction-button,
  .settings-button {
    top: 0;
  }

  .interaction-button {
    left: 2.16rem;
  }

  .settings-button {
    right: 2.16rem;
  }
}
</style>
